import { waitIfConcurrentAndReturnRes } from "../RunCraft/concurrent_wait_and_res";
import { address_to_script, getLog } from "../helpers/helpers";
import { useContext } from "react";


/**
 * This custom inventory should be much faster & reliable than run.inventory
 * as it should leverage the cache better & rely only on the utxos set of the owner address
 *
 * @param {function to get run asynchronously} get_run_asyncf
 
  joshua on slack:
    using inventory is inefficient and will likely be deprecated moving forward.
    to take advantage of the cache, you can query UTXOs on your owner address(es) and iterate,
    loading each jig individually via the location - that will look up the jig in the cache if
    present and is much faster + we can have a more precise loading-bar using that
 */
export class RunInventory {

    constructor(run) {
        this.classname = "RunInventory: "
        const log = getLog(this.classname)
        this.log = log

        // config vars
        this.debug = true

        // vars setup from params
        this.run = run

        // other internal vars
        this.length = 0
        this.utxos = []
        this.jigs_origins = {}  // set = no dups
        this.jigs = []

        log("created")
    }


    async sync(...args) {
        return await waitIfConcurrentAndReturnRes.call(this, this._sync, ...args)
    }


    async _addJig(jig) {
        const log = getLog(this.classname + "_addJig():")
        // check origin to make sure we never have dups jigs in inventory
        if (!this.jigs_origins[jig.origin]) {
            this.jigs_origins[jig.origin] = true  // add to set
            this.jigs.push(jig)
            this.length += 1
        }
    }


    async _loadAndAddJig(location) {
        const log = getLog(this.classname + "_loadAndAddJig(): ")
        const run = this.run
        try {
            if (this.debug) log("await run.load() for", location)
            let jig = await run.load(location)
            if (jig) {  // otherwise it was not trusted so run couldn't load it & will automatically display a warning in logs
                if (this.debug) log("loaded", location, ":", jig)
                this._addJig(jig)
            }
        } catch (error) {
            if (this.debug) log("failed to load, probably untrusted location", location)
        }
    }


    _check_class_origins_to_load(class_origins_to_load) {
        const function_id = this.classname + "_check_class_origins_to_load(): "
        const log = getLog(function_id)
        log("class_origins_to_load=", class_origins_to_load)

        let error_mess = null
        if (!class_origins_to_load || !Array.isArray(class_origins_to_load)) {
            error_mess = function_id + "invalid array class_origins_to_load, got type: "
                + typeof class_origins_to_load
        } else if (class_origins_to_load.length == 0) {
            error_mess = function_id + "invalid array class_origins_to_load, length should be > 0."
        }
        if (error_mess) {
            console.error(error_mess)
            throw new Error(error_mess)
        }
    }


    /* fetches utxos using CustomBlockchain and then syncs each jigs individually
        this allows us to optimize & use run's cache
        (since we retrieve utxos directly we just need to load them,
            no need to sync since they are already at the latest state)
        question: what about .codes ?
        question: what about destroyed jigs ?
    */
    async _sync(class_origins_to_load) {
        const function_id = this.classname + "sync(): "
        const log = getLog(function_id)

        log("class_origins_to_load=", class_origins_to_load)
        this._check_class_origins_to_load(class_origins_to_load)

        if (this.debug) log("start")
        let start_time = Date.now()

        if (!this.run) {
            const errormess = function_id + "this.run is null"
            console.error(errormess)
            throw new Error(errormess)
        }
        const run = this.run

        const owner_address = run.owner.address
        log("owner_address=", owner_address, "in", (Date.now() - start_time), "ms")

        const script = address_to_script(run.owner.address)
        log("owner address = ", run.owner.address, "from script=", script)

        /* to optimize this massively for multi-coin, we only request utxos for targeted (script, class_origin) from the node
        */
        log("class_origins_to_load=", class_origins_to_load)
        // Parallelize the per-class_origin utxos() calls — these are independent
        // fetches and were previously sequential, adding N round-trips for N
        // class origins. Currently only 1 origin is passed, but this scales
        // linearly with the number of token contracts supported.
        const utxo_results = await Promise.all(
            class_origins_to_load.map(class_origin => run.blockchain.utxos(script, class_origin))
        )
        let utxos = utxo_results.reduce((acc, new_utxos) => acc.concat(new_utxos), [])
        log("found", utxos.length, "utxos total across", class_origins_to_load.length, "class_origins")

        this.utxos = utxos
        log("found", utxos.length, "utxos in owner address in", (Date.now() - start_time), "ms")

        let nb_cached = 0, nb_banned = 0, nb_loaded = 0

        // useful to log them
        let list_banned_txs = []

        const load_utxo = async (i) => {
            let utxo = utxos[i]
            let location = utxo.txid + "_o" + utxo.vout
            if (this.debug) log("trying to load", location)

            // let cached_jig = await run.cache.get("jig://" + location)
            // let banned_jig = await run.cache.get("ban://" + location)
            ///* // changed 28.01.2023
            // optimize with parallel calls
            let cached_jig, banned_jig
            [cached_jig, banned_jig] = await Promise.all([
                run.cache.get("jig://" + location),
                run.cache.get("ban://" + location),
            ])
            //*/

            let is_not_banned = (banned_jig === false || banned_jig === undefined)
            let is_banned = !is_not_banned

            /* // TODO remove that, just for this.debug testing
            if (targetLocation) {
                log("targetLocation is_banned=", is_banned, " isCached=", !!cached_jig, "\n banned_jig=", banned_jig, "\n cached_jig=", cached_jig)
            } */

            if (is_banned) {
                if (this.debug) log("location seems banned", location, banned_jig)
                nb_banned++
                list_banned_txs.push(location)
            } else if (cached_jig) {
                if (this.debug) log("found cached_jig for", location, ":", cached_jig)
                nb_cached++;
                // this._addJig(cached_jig)
                await this._loadAndAddJig(location)
            } else {
                await this._loadAndAddJig(location)
            }

            nb_loaded++
            if (this.debug) log(nb_loaded, "/", utxos.length);
            else if (nb_loaded % 20 === 0) log(nb_loaded, '/', utxos.length)
        }

        // in parallel took 15s VS sequentially took 27s
        const all_promises = []
        for (let i = 0; i < utxos.length; i++) {
            all_promises.push(load_utxo(i))  // no await here to load in parallel
        }
        await Promise.all(all_promises)

        log("loaded", utxos.length, "utxos in ", (Date.now() - start_time), "ms,",
            nb_cached, "were cached and", nb_banned, "were already banned",
            "=> extracted", this.jigs.length, "jigs"
        )

        log("list_banned_txs=", list_banned_txs)
    }
}
